import { ComponentProps, FC, ReactNode } from "react";
import { Row, Alert } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { Form, Input } from "formik-antd";
import * as _ from "lodash";

import { classname } from "@ctra/utils";

import { Button } from "../../../elements";
import styles from "./ForgotPasswordForm.module.less";

export interface ForgotPasswordFormProps extends ComponentProps<typeof Form> {
  /**
   * translated labels
   */
  labels: {
    username: string;
    submit: string;
    fulfilled?: string;
  };
  /**
   * handle field blur
   * @param {string} fieldName
   */
  onFieldBlur: (fieldName: "username" | "password") => void;
  /**
   * loading state
   */
  isLoading?: boolean;
  /**
   * form extra, eg. link to other page
   */
  extra?: ReactNode;
}

/**
 * Test ids for unit testing
 * @type {{submit: string, emailField: string}}
 */
const testIds = {
  emailField: "emailField",
  submit: "submit"
};

/**
 * Forgot password form component
 * @param {string} username
 * @param {string} submit
 * @param {string | undefined} fulfilled
 * @param {boolean | undefined} isLoading
 * @param {(fieldName: ("username" | "password")) => void} onFieldBlur
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} extra
 * @param {string | undefined} className
 * @param {Pick<ForgotPasswordFormProps & {children?: React.ReactNode | undefined}, "prefixCls" | "colon" | "name" | "layout" | "labelAlign" | "labelWrap" | "labelCol" | "wrapperCol" | "form" | "size" | "disabled" | "scrollToFirstError" | "requiredMark" | "hideRequiredMark" | "initialValues" | "children" | "component" | "fields" | "validateMessages" | "onValuesChange" | "onFieldsChange" | "onFinish" | "onFinishFailed" | "validateTrigger" | "preserve" | "acceptCharset" | "action" | "autoComplete" | "encType" | "method" | "noValidate" | "target" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture">} rest
 * @return {JSX.Element}
 * @constructor
 */
const ForgotPasswordForm: FC<ForgotPasswordFormProps> & { testIds: typeof testIds } = ({
  labels: { username, submit, fulfilled },
  isLoading,
  onFieldBlur,
  extra = null,
  className,
  ...rest
}) => (
  <Form
    size="large"
    className={classname("ctra-auth-ForgotPasswordForm", styles.ForgotPasswordForm, className)}
    {...rest}
  >
    <Form.Item name="username">
      <Input
        data-testid={testIds.emailField}
        disabled={isLoading}
        type="email"
        name="username"
        multiple={true}
        required={true}
        placeholder={username}
        prefix={<MailOutlined />}
        onBlur={_.partial(onFieldBlur, "username")}
      />
    </Form.Item>
    <Button
      block
      data-testid={testIds.submit}
      type="primary"
      htmlType="submit"
      size="large"
      loading={isLoading}
      disabled={isLoading}
    >
      {submit}
    </Button>
    {fulfilled && <Alert closable showIcon type="success" message={fulfilled} className={styles.Extra} />}
    {extra && (
      <Row align="middle" justify="end" className={classname(styles.Extra, styles.Flat)}>
        {extra}
      </Row>
    )}
  </Form>
);

ForgotPasswordForm.testIds = testIds;

export default ForgotPasswordForm;
